/**
 * Hospital Staff Training & Health-Check Status (Restricted).
 * R39 C5.a3 (plan docs/plans/sector-operations.md §2 SO-P9, SO-P252, H§5).
 * Allowlisted fields only: no vaccine names, medical findings, results or free text.
 */

import { addDaysToKey } from './rosters.js'

export const IMMUNISATION_STATUSES = [
  'COMPLETE',
  'PARTIAL',
  'NOT_RECORDED',
] as const

export type ImmunisationStatus = (typeof IMMUNISATION_STATUSES)[number]

export interface OpsStaffStatusDoc {
  id?: string
  workspaceId: string
  personKey: string // w_{workerId} or m_{uid}
  inductionTrainingOn?: string | null // YYYY-MM-DD
  lastAnnualTrainingOn?: string | null // YYYY-MM-DD
  immunisationStatus: ImmunisationStatus
  healthCheckOn?: string | null // YYYY-MM-DD
  restricted: true
  createdAt?: unknown
  updatedAt?: unknown
  updatedBy?: string
}

export type AnnualTrainingStatus = 'OK' | 'DUE' | 'OVERDUE'

/**
 * Computes whether annual training is OK, DUE (within 30 days), or OVERDUE.
 * Annual training is due 365 days after lastAnnualTrainingOn.
 */
export function computeAnnualTrainingStatus(
  lastAnnualTrainingOn: string | null | undefined,
  todayDayKey: string,
): AnnualTrainingStatus {
  if (!lastAnnualTrainingOn || typeof lastAnnualTrainingOn !== 'string') {
    return 'OVERDUE'
  }
  const cleanLast = lastAnnualTrainingOn.trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(cleanLast)) {
    return 'OVERDUE'
  }

  const dueDay = addDaysToKey(cleanLast, 365)
  if (todayDayKey > dueDay) {
    return 'OVERDUE'
  }

  // Check if today is within 30 days of dueDay
  // dueDay minus 30 days <= todayDayKey <= dueDay
  const windowStartDay = addDaysToKey(dueDay, -30)
  if (todayDayKey >= windowStartDay) {
    return 'DUE'
  }

  return 'OK'
}

export function isValidImmunisationStatus(status: unknown): status is ImmunisationStatus {
  return typeof status === 'string' && IMMUNISATION_STATUSES.includes(status as ImmunisationStatus)
}

export function isValidIsoDayKey(dayKey: unknown): boolean {
  if (typeof dayKey !== 'string') return false
  return /^\d{4}-\d{2}-\d{2}$/.test(dayKey.trim())
}
